import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useParams } from "react-router-dom";
import {
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { useTranslation } from "react-i18next";

import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";

import VideoToolbar from "./components/VideoToolbar";
import SelectedFiltersBar from "./components/SelectedFiltersBar";
import VideoGrid from "./components/VideoGrid";
import VideoFiltersModal from "./components/VideoFiltersModal";

import { useVideoList } from "./hooks/useVideoList";
import { useInfiniteScrollTrigger } from "./hooks/useInfiniteScrollTrigger";
import { buildVideoNavigationPath } from "./helpers/videoNavigation";
import { getProjectConfig } from "../../projects";
import { PAGE_TOP_FILL, pageBackgroundUnderBar } from "../../theme/pageStyle";

// The tinted page, kept flat under the fixed search bar (108px with filter chips,
// its tallest) so the bar blends in.
const VIDEOS_BACKGROUND = pageBackgroundUnderBar(108);

const PAGE_SIZE = 24;
const allowedIds: string[] = [];

type RouteParams = {
  study_slug: string;
  title: string;
  type: string;
  contentType: string;
};

const DisplayVideos: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  const { study_slug, title, contentType } = useParams<RouteParams>();

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoverId, setHoverId] = useState<string | null>(null);

  const [savedOnly, setSavedOnly] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("saved") === "1";
  });

  const effectiveSelectedTags = useMemo(
    () => (savedOnly ? [] : selectedTags),
    [savedOnly, selectedTags]
  );

  // Filter sheet sections come from the project config. Entries go through
  // t(): i18n keys resolve, plain literals pass through unchanged. The
  // resolved strings are matched VERBATIM against Cloudflare video tags.
  // Projects without videoFilterSections get an empty filter sheet.
  const allTags: Record<string, string[]> = useMemo(
    () =>
      Object.fromEntries(
        (getProjectConfig().videoFilterSections ?? []).map((section) => [
          t(section.label),
          section.chips.map((chip) => t(chip)),
        ])
      ),
    [t]
  );

  const baseTags = useMemo(
    () => [title, contentType].filter(Boolean),
    [title, contentType]
  );

  // Lance le lecteur directement en entrant sur l'onglet Vidéos, sauf au retour
  // depuis le lecteur. État initial calculé de façon synchrone pour que le loader
  // s'affiche dès le 1er rendu (évite tout flash de la grille au lancement).
  const [autoLaunch, setAutoLaunch] = useState(() => {
    const returning =
      sessionStorage.getItem("videos_skip_autolaunch") === "1";
    const savedInit =
      new URLSearchParams(window.location.search).get("saved") === "1";
    return baseTags.length === 0 && !savedInit && !returning;
  });
  const autoLaunchedRef = useRef(false);

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: VIDEOS_BACKGROUND,
      headerBackground: PAGE_TOP_FILL,
      title: savedOnly
        ? "mentalplus_videos_enregistrees"
        : "mentalplus_tab_videos",
      welcomePage: false,
    });

    // On ne relance PAS le lecteur si on revient depuis lui (flag posé par le
    // bouton retour) ou si l'entrée se fait en arrière (back matériel/geste).
    const skip = sessionStorage.getItem("videos_skip_autolaunch");
    if (skip) sessionStorage.removeItem("videos_skip_autolaunch");
    const isBack = ionRouter.routeInfo?.routeDirection === "back";

    const canLaunch =
      !skip &&
      !isBack &&
      baseTags.length === 0 &&
      new URLSearchParams(window.location.search).get("saved") !== "1" &&
      !query &&
      selectedTags.length === 0;

    if (!canLaunch) {
      setAutoLaunch(false);
      return;
    }

    // Lancement INSTANTANÉ : si on connaît déjà la 1re vidéo (cache), on ouvre
    // le lecteur directement, sans passer par le loader ni la grille.
    const cachedUid = localStorage.getItem(`${study_slug}_firstVideoUid`);
    if (cachedUid) {
      autoLaunchedRef.current = true;
      ionRouter.push(
        `/${study_slug}/tabs/videos/${cachedUid}/default`,
        "forward",
        "push"
      );
      setAutoLaunch(false);
      return;
    }

    // 1re fois (pas de cache) : loader + fetch, puis lancement.
    autoLaunchedRef.current = false;
    setAutoLaunch(true);
  });

  useIonViewWillLeave(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      headerBackground: "transparent",
    });
  });

  useEffect(() => {
    const syncSavedOnlyFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      setSavedOnly(params.get("saved") === "1");
    };

    syncSavedOnlyFromUrl();

    window.addEventListener("popstate", syncSavedOnlyFromUrl);
    return () => window.removeEventListener("popstate", syncSavedOnlyFromUrl);
  }, [location.pathname]);

  useEffect(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: VIDEOS_BACKGROUND,
      headerBackground: PAGE_TOP_FILL,
      title: savedOnly
        ? "mentalplus_videos_enregistrees"
        : "mentalplus_tab_videos",
      welcomePage: false,
    });
  }, [savedOnly, setResonanceLayout]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [selectedTags, query, savedOnly]);

  useEffect(() => {
    if (savedOnly && selectedTags.length > 0) {
      setSelectedTags([]);
    }
    setPanelOpen(false);
  }, [savedOnly, selectedTags.length]);

  const { videos, loading, loadingMore, hasMore, loadMore } = useVideoList({
    baseTags,
    selectedTags: effectiveSelectedTags,
    query,
    allowedIds,
    pageSize: PAGE_SIZE,
    savedOnly,
  });

  const { lastItemRef } = useInfiniteScrollTrigger({
    hasMore,
    loading: loading || loadingMore,
    onLoadMore: loadMore,
  });

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((item) => item !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setPanelOpen(false);
  };

  const handleVideoClick = (videoUid: string) => {
    const path = buildVideoNavigationPath({
      studySlug: study_slug,
      title,
      contentType,
      videoUid,
      selectedTags: effectiveSelectedTags,
      search: query,
    });

    ionRouter.push(path, "forward", "push");
  };

  const isMainDefaultView =
    baseTags.length === 0 &&
    !savedOnly &&
    !query &&
    effectiveSelectedTags.length === 0;

  // Mémorise la 1re vidéo de la liste par défaut pour un lancement instantané
  // aux prochains accès (persiste entre les sessions).
  useEffect(() => {
    if (baseTags.length === 0 && !savedOnly && videos.length > 0) {
      localStorage.setItem(`${study_slug}_firstVideoUid`, videos[0].uid);
    }
  }, [videos, savedOnly, baseTags, study_slug]);

  // Auto-lancement du lecteur sur la 1re vidéo (onglet Vidéos principal uniquement).
  useEffect(() => {
    if (!autoLaunch) return;

    if (!isMainDefaultView) {
      // Contexte non lançable (saved / filtre / île) -> on révèle la grille.
      setAutoLaunch(false);
      return;
    }

    if (!loading && videos.length === 0) {
      // Aucune vidéo -> on révèle la grille (état vide).
      setAutoLaunch(false);
      return;
    }

    if (!autoLaunchedRef.current && videos.length > 0) {
      autoLaunchedRef.current = true;
      // On garde autoLaunch=true (loader) jusqu'à la navigation.
      handleVideoClick(videos[0].uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLaunch, videos, loading, isMainDefaultView]);

  // Tant qu'on lance le lecteur, on masque la grille (loader).
  const isLaunching = autoLaunch && isMainDefaultView;

  const handleOpenSaved = () => {
    const nextSavedOnly = !savedOnly;
    const nextUrl = nextSavedOnly
      ? `/${study_slug}/tabs/videos?saved=1`
      : `/${study_slug}/tabs/videos`;

    setSavedOnly(nextSavedOnly);
    ionRouter.push(nextUrl, nextSavedOnly ? "forward" : "back", "push");
  };

  const handleBackFromSaved = () => {
    setSavedOnly(false);
    ionRouter.push(`/${study_slug}/tabs/videos`, "back", "pop");
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        {isLaunching ? (
          <LauncherLoader>
            <ResonanceLoader />
          </LauncherLoader>
        ) : (
          <>
            <TopArea>
              {savedOnly ? (
                <BackBar>
                  <BackButton onClick={handleBackFromSaved}>
                    ← {t("mentalplus_video_retour")}
                  </BackButton>
                </BackBar>
              ) : (
                <>
                  <VideoToolbar
                    query={query}
                    onQueryChange={setQuery}
                    onOpenFilters={() => setPanelOpen(true)}
                    onOpenSaved={handleOpenSaved}
                  />

                  <SelectedFiltersBar
                    selectedTags={selectedTags}
                    onToggleTag={toggleTag}
                    onClear={clearFilters}
                  />
                </>
              )}
            </TopArea>

            <ContentArea $hasChips={!savedOnly && selectedTags.length > 0}>
              <VideoGrid
                videos={videos}
                loading={loading}
                loadingMore={loadingMore}
                hoverId={hoverId}
                onHoverChange={setHoverId}
                lastVideoRef={lastItemRef}
                onVideoClick={handleVideoClick}
                savedOnly={savedOnly}
              />
            </ContentArea>

            {!savedOnly && (
              <VideoFiltersModal
                isOpen={panelOpen}
                title={t("mentalplus_video_filtres")}
                sections={allTags}
                selectedTags={selectedTags}
                onClose={() => setPanelOpen(false)}
                onToggleTag={toggleTag}
              />
            )}
          </>
        )}
      </PageWrapper>
    </ResonanceLayout>
  );
};

export default DisplayVideos;

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  /* Transparent: the page frame paints the tint (VIDEOS_BACKGROUND). */
  background: transparent;
  position: relative;
  overflow-x: hidden;
`;

const LauncherLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
`;

const TopArea = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  width: 100%;
  /* Opaque (hides the grid scrolling beneath) in the tint's flat top color. */
  background: ${PAGE_TOP_FILL};
  z-index: 10000;
`;

const BackBar = styled.div`
  background: transparent;
  padding: 8px;
`;

const BackButton = styled.button`
  flex: 0 0 auto;
  min-width: 84px;
  height: 58px;
  border: none;
  border-radius: 14px;
  /* Action button -> secondary family (see tokens.ts roles). */
  background: var(--app-color-secondary, #ffce48);
  color: var(--app-color-secondary-contrast, #1b1b1b);
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 12px;
  line-height: 12px;
  font-weight: 600;
  white-space: nowrap;
  padding: 0 12px;

  @media (max-width: 420px) {
    min-width: 70px;
    height: 54px;
    font-size: 11px;
  }
`;

const ContentArea = styled.div<{ $hasChips: boolean }>`
  padding-top: ${({ $hasChips }) => ($hasChips ? "108px" : "62px")};
`;